import {
  type CategorySearchSuggestion,
  type GetFilterAggregatesQueryParams,
} from './aliases';

export type SearchSuggestionOption = CategorySearchSuggestion & {
  label: string;
};

export enum SearchSuggestionType {
  POPULAR = 'popular',
  RECENT = 'recent',
  POPULAR_WITH_CATEGORY = 'popularWithCategory',
  POPULAR_WITH_BRAND = 'popularWithBrand',
  NONE = 'none',
  TRENDING = 'trending',
  PRODUCT = 'products',
}

export type ActiveBrandIds = number[];
export type ActiveSizeIds = string[];
export type IsOnSale = boolean;
export type ActiveColours = string[];
export type ActiveConditions = string[];
export type ActivePrice = {
  min: number | null;
  max: number | null;
};

export type ActiveFilterValues = {
  activeGroup: string;
  activeProductTypes: string[];
  activeGender?: string;
  activeBrandIds: ActiveBrandIds;
  activeSizeIds: ActiveSizeIds;
  activePrice: ActivePrice;
  activeColours: ActiveColours;
  activeConditions: ActiveConditions;
  isOnSale: IsOnSale;
  isKids: boolean;
  sortBy: SearchSortByValues;
};

export type URLFilterParams = {
  groups?: string;
  productTypes?: string;
  gender?: string;
  isKids?: string;
  sizes?: string;
  brands?: string;
  isDiscounted?: string;
  priceMin?: string;
  priceMax?: string;
  colours?: string;
  conditions?: string;
  brandSlug?: string;
  categoryPath?: string[];
  sort?: SearchSortByValues;
};

export type UrlParams = {
  searchParams: URLSearchParams;
  pathname: string;
};

// Sort options used on shop
export enum ShopSortByValues {
  Product = 'product',
  ProductPriceDesc = 'product_price_desc',
  ProductPriceAsc = 'product_price_asc',
  Recent = 'recent',
  SellersRecommendations = 'sellers_recommendations', // This is not a domain concept but instead an app-level type
}

// Sort options used on browse pages
export enum SearchSortByValues {
  Relevance = 'relevance',
  PriceLowToHigh = 'priceAscending',
  PriceHighToLow = 'priceDescending',
  MostPopular = 'popular',
  NewlyListed = 'newlyListed',
}

// Runtime object which we can use to access the enum values e.g. SortByValues.Relevance
export const SortByValues = {
  ...ShopSortByValues,
  ...SearchSortByValues,
} as const;

export type SortBy = ShopSortByValues | SearchSortByValues;

const SHOP_SORTS = new Set<string>(Object.values(ShopSortByValues));
const SEARCH_SORTS = new Set<string>(Object.values(SearchSortByValues));

export function isShopSort(value: unknown): value is ShopSortByValues {
  return typeof value === 'string' && SHOP_SORTS.has(value);
}

export function isSearchSort(value: unknown): value is SearchSortByValues {
  return typeof value === 'string' && SEARCH_SORTS.has(value);
}

export function isSortBy(value: unknown): value is SortBy {
  return isShopSort(value) || isSearchSort(value);
}

export type DepopRequestMeta = {
  sessionId?: string;
};

export type FetchFilterAggregatesRequest = {
  params: GetFilterAggregatesQueryParams;
  meta: DepopRequestMeta;
};

export type Meta = {
  result_count: number;
  cursor?: string;
  has_more: boolean;
  total_count?: number;
};

type Aggregate = { count: number };
export type AggregateMap = Record<string, Aggregate>;

type SizeAggregates = Record<string, number>;

export type DepartmentAggregates = {
  count: number;
  groups: AggregateMap;
  product_types: AggregateMap;
};

export type FetchFilterAggregatesResponse = {
  total: number;
  departments: {
    menswear: DepartmentAggregates;
    womenswear: DepartmentAggregates;
    kidswear: DepartmentAggregates;
    everything_else?: DepartmentAggregates;
  };
  brands: AggregateMap;
  colours: AggregateMap;
  conditions: AggregateMap;
  groups?: AggregateMap;
  sizes: SizeAggregates;
};

export type ProductAttribute = {
  id: string;
  status: string;
};

export type ConditionAttribute = ProductAttribute & {
  nameI18N: string;
  descriptionI18N: string;
};

export type ColourAttribute = ProductAttribute & {
  nameI18N: string;
  hexCode: string;
};

export type StyleOption = ProductAttribute & {
  name: string;
};

export type StyleAttribute = {
  nameI18N: string;
  descriptionI18N: string;
  status: string;
  maxSelection: number;
  options: StyleOption[];
};

export type BrandAttribute = StyleOption[];

export type YouMightBeIntoPillAttributes = {
  label: string;
  href: string;
};

export enum SearchFrom {
  InCountrySearch = 'in_country_search',
  RecentSearch = 'RecentSearch',
}

export type UserSearchRequest = {
  enabled?: boolean;
  isInExperiment?: boolean;
  query?: string;
};
